import { Button } from "@/components/ui/button";
import {
  ArrowDown,
  ArrowUp,
  Barcode,
  Braces,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Lock,
  QrCode,
  Square,
  Minus,
  Trash2,
  Type,
  Unlock,
} from "lucide-react";

import type { EtiquetaElemento } from "@/types/etiqueta-designer";
import { rotuloElemento } from "@/utils/zplDesigner";

const ICONE = {
  texto: Type,
  barras: Barcode,
  qrcode: QrCode,
  imagem: ImageIcon,
  caixa: Square,
  linha: Minus,
  bruto: Braces,
} as const;

type Props = {
  elementos: EtiquetaElemento[];
  selecionadoId: string | null;
  onSelecionar: (id: string) => void;
  onPatch: (id: string, patch: Record<string, unknown>) => void;
  onExcluir: (id: string) => void;
  onMoverOrdem: (id: string, direcao: -1 | 1) => void;
};

export function PainelCamadas({
  elementos,
  selecionadoId,
  onSelecionar,
  onPatch,
  onExcluir,
  onMoverOrdem,
}: Props) {
  if (!elementos.length) {
    return (
      <p className="p-4 text-center text-xs text-muted-foreground">
        Nenhum elemento. Use a barra de ferramentas para adicionar.
      </p>
    );
  }

  // topo da lista = último desenhado (fica por cima)
  const ordenados = [...elementos].reverse();

  return (
    <ul className="divide-y">
      {ordenados.map((el) => {
        const Icone = ICONE[el.tipo] ?? Type;
        const ativo = el.id === selecionadoId;
        return (
          <li
            key={el.id}
            className={`flex items-center gap-1.5 px-2 py-1.5 cursor-pointer ${
              ativo ? "bg-accent" : "hover:bg-accent/50"
            }`}
            onClick={() => onSelecionar(el.id)}
          >
            <Icone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span
              className={`flex-1 truncate text-xs ${
                el.oculto ? "line-through opacity-50" : ""
              }`}
              title={rotuloElemento(el)}
            >
              {rotuloElemento(el)}
            </span>
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground"
              title="Trazer para frente"
              onClick={(e) => {
                e.stopPropagation();
                onMoverOrdem(el.id, 1);
              }}
            >
              <ArrowUp className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground"
              title="Enviar para trás"
              onClick={(e) => {
                e.stopPropagation();
                onMoverOrdem(el.id, -1);
              }}
            >
              <ArrowDown className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground"
              title={el.oculto ? "Mostrar" : "Ocultar"}
              onClick={(e) => {
                e.stopPropagation();
                onPatch(el.id, { oculto: !el.oculto });
              }}
            >
              {el.oculto ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
            </button>
            <button
              type="button"
              className="p-1 text-muted-foreground hover:text-foreground"
              title={el.bloqueado ? "Desbloquear" : "Bloquear"}
              onClick={(e) => {
                e.stopPropagation();
                onPatch(el.id, { bloqueado: !el.bloqueado });
              }}
            >
              {el.bloqueado ? (
                <Lock className="h-3 w-3" />
              ) : (
                <Unlock className="h-3 w-3" />
              )}
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-500/10"
              title="Excluir"
              onClick={(e) => {
                e.stopPropagation();
                onExcluir(el.id);
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
